/** Utility functions for keyboard navigation, separated from React hook logic. */
import {
  handleReorder,
  keyActions,
} from '@/features/tasks/utils/interactions/listKeyboardActions';

const isInputOrTextarea = () => {
  const activeElement = document.activeElement;
  return (
    activeElement &&
    (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
  );
};

const isReorderKeyPress = (event) =>
  (event.ctrlKey || event.metaKey) &&
  (event.key === 'ArrowDown' || event.key === 'ArrowUp');

const getKeyString = (event) => {
  const key = event.key === ' ' ? 'Space' : event.key;
  const isCtrl = event.ctrlKey || event.metaKey;
  return isCtrl ? `Ctrl+${key}` : key;
};

const isArrowKey = (key) => key === 'ArrowDown' || key === 'ArrowUp';

const handleArrowNavigation = (state, event) => {
  event.preventDefault();
  const isDown = event.key === 'ArrowDown';
  const newIndex = isDown
    ? Math.min(state.selectedIndex + 1, state.filteredTasks.length - 1)
    : Math.max(state.selectedIndex - 1, 0);

  const taskToSelect = state.filteredTasks[newIndex];
  if (taskToSelect && taskToSelect.id) {
    state.setSelectedIndex(taskToSelect.id);
  }
};

const handleReorderKey = (state, event) => {
  event.preventDefault();
  const direction = event.key === 'ArrowDown' ? 'down' : 'up';
  handleReorder(direction, state);
};

export const processKeyDown = (
  event,
  stateRef,
  moveTask,
  user,
  previousSelectedIndexRef,
  selectedTaskIdBeforeReorder,
) => {
  if (!stateRef.current.isFocused || isInputOrTextarea()) return;

  // Two calling conventions reach this wrapper from handleReorder:
  // - generic single-sibling reorder: updateTask(id, {}, userId, {previousKey, nextKey})
  //   → routes through onMove, which derives the orderKey via safeGenerateKeyBetween.
  // - today/plan multi-field cascade reorder: updateTask(id, fields, userId) with no
  //   4th arg → fields (e.g. {date, orderKey}) must reach the real mutation directly,
  //   not onMove (which only knows how to set a single orderKey).
  const realUpdateTask = stateRef.current.updateTask;
  const updateTask = (taskId, data, _userId, newPosition) => {
    if (newPosition) {
      stateRef.current.onMove(taskId, newPosition);
      return;
    }
    if (realUpdateTask) realUpdateTask(taskId, data, user?.uid);
  };

  const state = {
    ...stateRef.current,
    previousSelectedIndexRef,
    selectedTaskIdBeforeReorder,
    updateTask,
    moveTask,
    userId: user?.uid,
  };

  if (isReorderKeyPress(event)) {
    handleReorderKey(state, event);
  } else if (isArrowKey(event.key)) {
    handleArrowNavigation(state, event);
  } else {
    const action = keyActions[getKeyString(event)] ?? keyActions[event.key];
    if (action) action(state, event);
  }
};
